/**
 * useReveal.js — Scroll-driven animation hooks.
 *
 * Provides reveal, parallax, staggered-reveal, and scroll-progress hooks,
 * all reduced-motion aware.
 */
import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useReveal — Reveal a container element when it scrolls into view.
 *
 * Attach the returned ref, then add the --in class while `visible` is true
 * to trigger CSS transitions.
 *
 * @param {object}  [opts]
 * @param {number}  [opts.threshold=0.12]   Fraction (0–1) visible before firing.
 * @param {boolean} [opts.once=true]        Fire only once.
 * @param {string}  [opts.rootMargin="0px"] IntersectionObserver rootMargin.
 * @returns {[React.RefObject, boolean]} [ref, visible]
 */
export function useReveal({ threshold = 0.12, once = true, rootMargin = "0px" } = {}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (visible && once) return;

    // Skip the animation entirely when the user prefers reduced motion.
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setVisible(true);
      return;
    }

    const obs = new window.IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          if (once) obs.disconnect();
        } else if (!once) {
          setVisible(false);
        }
      },
      { threshold, rootMargin }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold, once, visible, rootMargin]);

  return [ref, visible];
}

/**
 * useParallax — Subtle parallax translate + scale for hero decoration.
 *
 * Returns a ref callback rather than a style object. The transform is written
 * straight to the DOM inside a requestAnimationFrame, so scrolling never
 * triggers a React render — the previous version called setState on every
 * scroll frame, re-rendering the whole hero subtree each time. The visual
 * result is identical. Register as many nodes as needed; all get the same
 * transform.
 *
 * @param {number} [maxY=32]       Max pixel translation.
 * @param {number} [maxScale=0.04] Max scale delta.
 * @returns {(el: HTMLElement|null) => void} Ref callback for each parallax node.
 */
export function useParallax(maxY = 32, maxScale = 0.04) {
  const nodes = useRef(new Set());
  const rafRef = useRef(null);

  const register = useCallback((el) => {
    if (el) nodes.current.add(el);
  }, []);

  useEffect(() => {
    const set = nodes.current;
    // No parallax when the user prefers reduced motion.
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    function tick() {
      const scrollY = window.scrollY;
      const y = Math.min(scrollY * 0.18, maxY);
      const scale = 1 + Math.max(-maxScale, Math.min(0, -scrollY * 0.0007));
      const transform = `translateY(${y}px) scale(${scale})`;
      set.forEach((el) => { el.style.transform = transform; });
      rafRef.current = null;
    }

    function onScroll() {
      if (!rafRef.current) {
        rafRef.current = requestAnimationFrame(tick);
      }
    }

    tick(); // Set initial transform before the first scroll event.
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      set.forEach((el) => { el.style.transform = ""; });
      set.clear();
    };
  }, [maxY, maxScale]);

  return register;
}

/**
 * useStaggeredReveal — Reveal a list whose children animate with staggered delays.
 *
 * Apply `--d: calc(index * 0.07s)` on each child to drive the CSS stagger.
 *
 * @param {object}  [opts]
 * @param {number}  [opts.threshold=0.1] IntersectionObserver threshold.
 * @param {number}  [opts.staggerMs=70]  Delay between items, in ms.
 * @param {boolean} [opts.once=true]     Reveal only once.
 * @returns {[React.RefObject, boolean, number]} [ref, visible, staggerMs]
 */
export function useStaggeredReveal({ threshold = 0.1, staggerMs = 70, once = true } = {}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (visible && once) return;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setVisible(true);
      return;
    }

    const obs = new window.IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          if (once) obs.disconnect();
        } else if (!once) {
          setVisible(false);
        }
      },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold, once, visible]);

  return [ref, visible, staggerMs];
}

/**
 * useScrollProgress — Track scroll progress through a section as a 0–1 value.
 *
 * Useful for progress indicators or section-based colour changes.
 *
 * @returns {[React.RefObject, number]} [ref, progress]
 */
export function useScrollProgress() {
  const ref = useRef(null);
  const [progress, setProgress] = useState(0);
  const rafRef = useRef(null);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    function tick() {
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const winH  = window.innerHeight;
      const total = rect.height + winH;
      const p = Math.max(0, Math.min(1, (winH - rect.top) / total));
      setProgress(p);
      rafRef.current = null;
    }

    function onScroll() {
      if (!rafRef.current) {
        rafRef.current = requestAnimationFrame(tick);
      }
    }

    tick();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return [ref, progress];
}